// Librairies
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';

// Interfaces
import { Planning } from 'src/app/interfaces/api/planning';
import { defaultProject, Project } from 'src/app/interfaces/api/project';

// Classes
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Pop } from 'src/app/classes/shared/pop';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { PlanningDialogComponent } from './dialogs/planning-dialog/planning-dialog.component';

@Component({
  selector: 'app-plannings',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    TranslateModule,
    TimeFormatPipe,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    NoDataComponent
  ],
  templateUrl: './plannings.component.html',
  styleUrls: ['./plannings.component.scss']
})

export class PlanningsComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly router: Router = inject(Router);
  private readonly pop: Pop = inject(Pop);

  // Component API
  @ViewChild('planningPaginator', { static: false }) planningPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();

  // Variables
  loading: boolean = false;
  plannings: Planning[] = [];
  planningsCount: number = 0;
  planningsData: MatTableDataSource<Planning> = new MatTableDataSource();
  planningsColumns: string[] = ['name', 'model', 'tasks', 'date', 'actions'];
  project: Project = defaultProject;
  destroy$ = new Subject<void>();
  searchText: string = '';
  helpLink: string = 'https://cad42.atlassian.net/wiki/x/AYBhKQ';

  constructor() {
    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
      if (this.project && this.project.id !== project.id)
        this.loadPlannings();

      this.project = project;
    })
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.loadPlannings();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPlannings() {
    this.loading = true;

    this.planningService.getPlannings().pipe(takeUntil(this.destroy$)).subscribe(plannings => {
      this.plannings = plannings;
      this.planningsCount = plannings.length;

      this.plannings.forEach(planning => {
        this.planningService.countTasks(planning.id).pipe(takeUntil(this.destroy$)).subscribe(count => {
          planning.tasksCount = count;
        })
      })

      this.planningsData = new MatTableDataSource(this.plannings);

      this.planningsData.sort = this.sort;
      this.planningsData.paginator = this.planningPaginator;
      this.loading = false;
    })
  }

  selectCurrentPlanning(planningId: string) {
    localStorage.setItem('planningId', planningId);
    this.router.navigate(['/project/planning'], { queryParams: { id: planningId } })
  }

  search(text: string = "") {
    if (text !== this.searchText) {
      this.searchText = text;

      if (this.planningPaginator) {
        this.planningPaginator.pageIndex = 0;
      }

      this.planningsData.filter = this.searchText.trim().toLowerCase();
    }
  }

  editPlanning(planning: Planning, duplicate: boolean) {
    const modal = this.dialog.open(PlanningDialogComponent, {
      width: '800px',
      data: {
        planning: { ...planning },
        duplicate,
        mode: duplicate ? 'duplicate' : 'edit'
      }
    });

    modal.afterClosed().subscribe(res => {
      if (!res) this.loadPlannings();
    })
  }

  add() {
    const modal = this.dialog.open(PlanningDialogComponent, {
      width: '800px',
      data: { 
        planning: { name: "" },
        mode: 'add'
       }
    });

    modal.afterClosed().subscribe(res => {
      if (!res) this.loadPlannings();
    })
  }

  deletePlanning(planning: Planning) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        title: this.translate.instant('planning.details'),
        name: planning.name
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.planningService.removePlanning(planning.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.plannings = this.plannings.filter(p => p.id !== planning.id);
            this.planningsData.data = this.plannings;
            this.pop.success(this.translate.instant('planning.deletePlanning_success'), 'success', 'delete');
          },
          error: (err) => {
            console.error(err);
            this.pop.error(this.translate.instant('planning.deletePlanning_error'));
          }
        });
      }
    });
  }
}
